import { IInitializable, IStaticClassName } from "./Initializable";

declare global {
    interface Window {
        Initializer: Initializer;
    }
}

export class Initializer {
    private registry: Map<string, IStaticClassName> = new Map();
    private initialized: Map<string, IInitializable> = new Map();

    private static instance: Initializer;

    private constructor() {
        window.Initializer = this;
    }

    public static getInstance(): Initializer {
        return this.instance || (this.instance = new this());
    }

    static init() {
        this.getInstance().init();
    }

    public init() {
        // Pages and blocks are initialized first, as they are usually the root elements of components.
        this.initComponents('[class*="-page"]:not([data-inited]), [class*="-block"]:not([data-inited])');

        // This is to initlaze components that are not child elements of pages or blocks.
        this.initComponents('[class*="-component"]:not([data-inited])');
    }

    private initComponents(
        selector: string,
    ) {
        const elements = document.querySelectorAll<HTMLElement>(selector);

        if (!elements) {
            return [];
        }

        return Array.from(elements).map(this.initComponent);
    }

    public static initComponent<T extends IInitializable>(element: HTMLElement): T | null {
        return this.getInstance().initComponent(element);
    }

    public initComponent = <T extends IInitializable>(element: HTMLElement): T | null => {
        const className = this.getClassName(element);

        if (!className) {
            console.warn(`Element does not have a class name starting with "js-" and ending with "-component", "-block", or "-page": ${element.className}`);
            return null;
        }

        const registeredClass = this.registry.get(className);

        if (!registeredClass) {
            console.warn(`${className} not registered`);
            return null;
        }

        const index = this.initialized.size;
        const key = `${className}-${index}`;
        const instance = new registeredClass(element, index) as T;
        // The instance might replace the element, so we cannot assume the element is still the same.
        instance.getRootElement().setAttribute("data-inited", key);
        this.initialized.set(key, instance);
    
        return instance;
    }

    private getClassName(element: HTMLElement) {
        return Array.from(element.classList).find(
            (className) => className.startsWith("js-") && (className.endsWith("-component") || className.endsWith("-block") || className.endsWith("-page"))
        );
    }

    static getComponent<T extends IInitializable>(rootElement: HTMLElement) {
        return this.getInstance().getComponent<T>(rootElement);
    }

    public getComponent<T extends IInitializable>(rootElement: HTMLElement): T | null {
        const key = rootElement.getAttribute("data-inited");
    
        if (key) {
            const component = this.initialized.get(key) as T | undefined || null;
            if (component) {
                return component;
            }
        }

        return this.initComponent<T>(rootElement);
    }

    /**
     * Registers a class with a static property `className`.
     * This method can be called with a single class or an array of classes.
     * @param classToRegister - The class to register. It must have a static property `className` that is a string.
     */
    public static register(classToRegister: IStaticClassName[]): void;
    public static register(classToRegister: IStaticClassName | IStaticClassName[]) {
        if (Array.isArray(classToRegister)) {
            classToRegister.forEach((cls) => this.getInstance().register(cls));
            return;
        }
        this.getInstance().register(classToRegister);
    }

    public register(classToRegister: IStaticClassName): void {
        this.registry.set(classToRegister.className, classToRegister);
    }
}
